import type { AxisOption, AxisOptionGroup } from 'components/molecules/PackArchitecture/AxisConfigDropdown';
import type { SortByOption } from 'components/molecules/PackArchitecture/axisConfiguration.utils';
import type {
    AxisGroupByControlsMeta,
    AxisSortByOptionMeta,
} from 'components/molecules/PackArchitecture/axisGroupByControlsMeta.types';

import type {
    PpaMetaControlNode,
    PpaMetaOptionItem,
    PricePackArchitectureMetaResponse,
} from './packArchitectureMeta.types';

const SORT_BY_API_IDS = new Set<string>([
    'sort_on_alphabet',
    'sort_on_value_sales',
    'sort_on_volume_sales',
    'sort_on_pack_size',
]);

const normalizeDimensionId = (id?: string | null) => id?.trim().toLowerCase() ?? '';

const getOwnDimensionSortEligibleIds = (checkbox?: PpaMetaControlNode | null): Set<string> => {
    const ids = new Set<string>();
    for (const option of checkbox?.options ?? []) {
        const normalized = normalizeDimensionId(option.id);
        if (normalized) ids.add(normalized);
    }
    return ids;
};

const isOwnDimensionSortEligible = (checkbox: PpaMetaControlNode | undefined, selectedXAxisId: string) =>
    getOwnDimensionSortEligibleIds(checkbox).has(normalizeDimensionId(selectedXAxisId));

/** The selected attribute's own label from the `own_dimension_sort` checkbox's
 * per-attribute option list (e.g. `vendor` -> "Vendor", `brand` -> "Brands"),
 * so the "Show my {Dimension} first" text stays in sync with that config. */
const getOwnDimensionSortOptionLabel = (
    checkbox: PpaMetaControlNode | undefined,
    selectedXAxisId: string,
): string | undefined => {
    const normalizedSelected = normalizeDimensionId(selectedXAxisId);
    return (checkbox?.options ?? []).find((option) => normalizeDimensionId(option.id) === normalizedSelected)?.label ?? undefined;
};

const toSortByOption = (id?: string | null): SortByOption => {
    if (id && SORT_BY_API_IDS.has(id)) {
        return id as SortByOption;
    }
    return 'sort_on_alphabet';
};

export const resolveGroupByTemplateLabel = (label: string, dimensionLabel: string) =>
    label.replace(/\{\$?[#/]?[^}]*group_by_options[^}]*\}/gi, dimensionLabel);

const toAxisOption = (item: PpaMetaOptionItem): AxisOption => ({
    label: item.label ?? item.id ?? '',
    value: item.id ?? '',
    suffix: item.suffix?.trim() || undefined,
});

export const findMetaOptionGroup = (meta: PricePackArchitectureMetaResponse | undefined, id: string) =>
    meta?.options?.find((option) => option.id === id);

export const findAxisConfig = (meta: PricePackArchitectureMetaResponse | undefined, axisId: string) => {
    const axisGroup = findMetaOptionGroup(meta, 'axis');
    return axisGroup?.options?.find((option) => option.id === axisId);
};

export const getXAxisGroupBySection = (xAxis?: PpaMetaControlNode, sectionId = 'group_by_options') =>
    xAxis?.options?.find((section) => section.id === sectionId);

export const getXAxisGroupByOptions = (xAxis?: PpaMetaControlNode, sectionId = 'group_by_options') =>
    getXAxisGroupBySection(xAxis, sectionId)?.group_by_options ?? [];

/** The selected attribute's `plural_label` straight from its `group_by_options`
 * config entry (e.g. `Vendor` -> "Vendors"), so pluralized copy (modal title,
 * "Group rest as Other {plural}", etc.) is driven entirely by the backend
 * config. Falls back to the singular `dimensionLabel` when unset. */
const getConfiguredPluralLabel = (
    xAxis: PpaMetaControlNode | undefined,
    selectedXAxisId: string,
    sectionId: string,
): string | undefined => {
    const normalizedSelected = normalizeDimensionId(selectedXAxisId);
    return (
        getXAxisGroupByOptions(xAxis, sectionId).find(
            (option) => normalizeDimensionId(option.id) === normalizedSelected,
        )?.plural_label ?? undefined
    );
};

const buildSortByMeta = (sortByNode?: PpaMetaControlNode) => {
    const sortOptions: AxisSortByOptionMeta[] = (sortByNode?.options ?? []).map((option) => ({
        id: toSortByOption(option.id),
        label: option.label ?? '',
        defaultSelected: option.default_value === true,
        orderOptions: (option.options ?? []).map((orderOption) => ({
            id: orderOption.id === 'desc' ? 'desc' : 'asc',
            label: orderOption.label ?? '',
            defaultSelected: orderOption.default_value === true,
        })),
    }));

    return { label: sortByNode?.label ?? 'Sort By', options: sortOptions };
};

const buildShowTopMeta = (showTopNode: PpaMetaControlNode | undefined, pluralDimensionLabel: string) => {
    if (!showTopNode) return null;

    const showTopSlider = showTopNode.options?.[0];
    const consolidateCheckbox = findNestedControl(showTopNode.options ?? null, 'consolidate_as_others');

    return {
        label: showTopNode.label ?? showTopSlider?.label ?? 'Show Top',
        step: showTopSlider?.step ?? 1,
        consolidateAsOthers: {
            label: resolveGroupByTemplateLabel(consolidateCheckbox?.label ?? '', pluralDimensionLabel),
            defaultSelected: consolidateCheckbox?.default_value === true,
        },
    };
};

const buildOwnDimensionSortMeta = (
    ownDimensionNode: PpaMetaControlNode | undefined,
    selectedXAxisId: string,
    pluralDimensionLabel: string,
) => {
    const ownDimensionCheckbox = ownDimensionNode?.options?.[0];
    const isEligible = Boolean(
        ownDimensionCheckbox && selectedXAxisId && isOwnDimensionSortEligible(ownDimensionCheckbox, selectedXAxisId),
    );
    if (!isEligible || !ownDimensionCheckbox) return null;

    const attributeLabel = getOwnDimensionSortOptionLabel(ownDimensionCheckbox, selectedXAxisId) ?? pluralDimensionLabel;

    return {
        sectionLabel: ownDimensionNode?.label ?? 'Preview',
        label: resolveGroupByTemplateLabel(ownDimensionCheckbox.label ?? '', attributeLabel),
        defaultSelected: ownDimensionCheckbox.default_value === true,
    };
};

/**
 * The selected group-by attribute's own `sort_by` node (e.g. Pack Range's
 * Pack Size / Value Sales / Volume Sales tree), when it carries one. Takes
 * precedence over the section-level `sort_by` shared by every other
 * attribute (e.g. A-Z / Value Sales / Volume Sales).
 */
const findAttributeSortByNode = (
    xAxis: PpaMetaControlNode | undefined,
    sectionId: string,
    selectedXAxisId: string,
): PpaMetaControlNode | undefined => {
    if (!selectedXAxisId) return undefined;
    const selectedOption = getXAxisGroupByOptions(xAxis, sectionId).find(
        (option) => option.id === selectedXAxisId,
    );
    return selectedOption?.options?.find((option) => option.id === 'sort_by');
};

export const buildAxisGroupByControlsMeta = (
    xAxis?: PpaMetaControlNode,
    dimensionLabel = '',
    selectedXAxisId = '',
    sectionId = 'group_by_options',
): AxisGroupByControlsMeta | null => {
    const controls = getXAxisGroupBySection(xAxis, sectionId)?.options;
    if (!controls?.length) return null;

    const sortByNode = controls.find((control) => control.id === 'sort_by');
    const attributeSortByNode = findAttributeSortByNode(xAxis, sectionId, selectedXAxisId);
    const showTopNode = controls.find((control) => control.id === 'show_top_n');
    const ownDimensionNode = controls.find((control) => control.id === 'own_dimension_sort');
    const pluralDimensionLabel = getConfiguredPluralLabel(xAxis, selectedXAxisId, sectionId) ?? dimensionLabel;

    return {
        pluralLabel: pluralDimensionLabel,
        sortBy: buildSortByMeta(attributeSortByNode ?? sortByNode),
        showTop: buildShowTopMeta(showTopNode, pluralDimensionLabel),
        ownDimensionSort: buildOwnDimensionSortMeta(ownDimensionNode, selectedXAxisId, pluralDimensionLabel),
    };
};

export const getAxisConfigurationDefaultsFromMeta = (controlsMeta: AxisGroupByControlsMeta | null | undefined) => {
    if (!controlsMeta) return undefined;

    const defaultSortOption =
        controlsMeta.sortBy.options.find((option) => option.defaultSelected) ?? controlsMeta.sortBy.options[0];
    const defaultOrder =
        defaultSortOption?.orderOptions.find((option) => option.defaultSelected)?.id ??
        defaultSortOption?.orderOptions[0]?.id ??
        'asc';

    return {
        sortBy: defaultSortOption?.id,
        sortOrder: defaultOrder,
        groupRestAsOther: controlsMeta.showTop?.consolidateAsOthers.defaultSelected ?? false,
        showOwnVendorFirst: controlsMeta.ownDimensionSort?.defaultSelected ?? false,
    };
};

export const getXAxisColumnOptionsSection = (xAxis?: PpaMetaControlNode, sectionId = 'column_options') =>
    xAxis?.options?.find((item) => item.id === sectionId);

export const getXAxisColumnOptions = (xAxis?: PpaMetaControlNode) => {
    const section = getXAxisColumnOptionsSection(xAxis);
    return section?.options?.[0]?.column_options ?? section?.column_options ?? [];
};

export const findNestedControl = (
    nodes: PpaMetaControlNode[] | null | undefined,
    id: string,
): PpaMetaControlNode | undefined => {
    if (!nodes?.length) return undefined;

    for (const node of nodes) {
        if (node.id === id) return node;
        const nested = findNestedControl(node.options ?? null, id);
        if (nested) return nested;
    }

    return undefined;
};

export const buildColumnOptionsGroups = (items: PpaMetaOptionItem[], fallbackTitle = ''): AxisOptionGroup[] => {
    if (!items.length) return [];

    const hasDimensionLabels = items.some((item) => item.dimension_label);

    if (!hasDimensionLabels) {
        return [{ title: '', options: items.map(toAxisOption) }];
    }

    const groupsByLabel = new Map<string, PpaMetaOptionItem[]>();
    for (const item of items) {
        const groupTitle = item.dimension_label ?? fallbackTitle;
        const existing = groupsByLabel.get(groupTitle) ?? [];
        existing.push(item);
        groupsByLabel.set(groupTitle, existing);
    }

    return Array.from(groupsByLabel.entries()).map(([title, groupItems]) => ({
        title,
        options: groupItems.map(toAxisOption),
    }));
};

export const mapAttributeOptionsToGroups = (items: PpaMetaOptionItem[]): AxisOptionGroup[] =>
    buildColumnOptionsGroups(items);

export const getXAxisKpiOptionIds = (xAxisConfig?: PpaMetaControlNode): string[] =>
    getXAxisColumnOptions(xAxisConfig)
        .map((item) => item.id)
        .filter((id): id is string => Boolean(id));

export const buildXAxisOptionGroups = (xAxis?: PpaMetaControlNode): AxisOptionGroup[] => {
    if (!xAxis) return [];

    return [
        ...buildColumnOptionsGroups(getXAxisGroupByOptions(xAxis)),
        ...buildColumnOptionsGroups(getXAxisColumnOptions(xAxis)),
    ];
};

export const buildYAxisOptionGroups = (yAxis?: PpaMetaControlNode) =>
    buildColumnOptionsGroups(yAxis?.column_options ?? []);

export const buildBubbleRepresentsOptionGroups = (axis?: PpaMetaControlNode) =>
    buildColumnOptionsGroups(axis?.group_by_options ?? []);

export const buildBubbleSizeOptionGroups = (axis?: PpaMetaControlNode) =>
    buildColumnOptionsGroups(axis?.column_options ?? []);

export const getDefaultOptionId = (items: PpaMetaOptionItem[]) =>
    items.find((item) => item.is_default)?.id ?? items[0]?.id ?? '';

export type SalesKpiIds = {
    valueSales: string;
    volumeSales: string;
    unitSales: string;
};

/** BRD PA-07 axis preview/sort aggregates use pos_data totals. */
export const AXIS_PREVIEW_SALES_KPI_IDS: SalesKpiIds = {
    valueSales: 'total_value',
    volumeSales: 'total_eq_volume',
    unitSales: 'total_units',
};

/** Table metric checkboxes from meta `ppa_grid` → `table_kpi` (PA-02). */
export const getTableKpiOptionItems = (meta?: PricePackArchitectureMetaResponse): PpaMetaControlNode[] => {
    const gridNode = findMetaOptionGroup(meta, 'ppa_grid');
    const tableKpiNode = gridNode?.options?.find((option) => option.id === 'table_kpi');
    return tableKpiNode?.options ?? [];
};

/** Table info tooltip from meta `ppa_grid` node. */
export const getTableTooltip = (meta?: PricePackArchitectureMetaResponse): string => {
    const gridNode = findMetaOptionGroup(meta, 'ppa_grid');
    return gridNode?.tooltip?.trim() ?? '';
};

export const getHeatMapToggleConfig = (meta: PricePackArchitectureMetaResponse | undefined) => {
    const toggleGroup = findMetaOptionGroup(meta, 'heat_map_toggle') ?? findMetaOptionGroup(meta, 'toggle');
    return toggleGroup?.options?.find((option) => option.id === 'enable_heat_map');
};

export const getAxisLabel = (axisValue: string, optionGroups: AxisOptionGroup[]) => {
    for (const group of optionGroups) {
        const match = group.options.find((option) => option.value === axisValue);
        if (match) return match.label;
    }
    if (!axisValue) return '';
    return axisValue
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

const findAxisOption = (axisValue: string, optionGroups: AxisOptionGroup[]) => {
    for (const group of optionGroups) {
        const match = group.options.find((option) => option.value === axisValue);
        if (match) return match;
    }
    return undefined;
};

/**
 * Axis/table legend label including the meta `suffix` unit (e.g. "Average Price/Unit ($)").
 * The suffix is intentionally omitted from the dropdown option labels and only shown here.
 */
export const getAxisLabelWithSuffix = (axisValue: string, optionGroups: AxisOptionGroup[]) => {
    const match = findAxisOption(axisValue, optionGroups);
    const label = getAxisLabel(axisValue, optionGroups);
    return match?.suffix ? `${label} (${match.suffix})` : label;
};

export const getAxisGroupTitle = (axisValue: string, optionGroups: AxisOptionGroup[]) => {
    for (const group of optionGroups) {
        if (group.options.some((option) => option.value === axisValue)) {
            return group.title?.trim() ?? '';
        }
    }
    return '';
};

export type ChartTitleSegment = { text: string; emphasis?: boolean };

export type PackArchitectureChartTitleInput = {
    xAxis: string;
    xAxisConfig?: PpaMetaControlNode;
    xAxisOptionGroups: AxisOptionGroup[];
    yAxis: string;
    yAxisOptionGroups: AxisOptionGroup[];
    bubbleRepresents: string;
    bubbleRepresentsOptionGroups: AxisOptionGroup[];
};

export const buildPackArchitectureChartTitleSegments = ({
    xAxis,
    xAxisConfig,
    xAxisOptionGroups,
    yAxis,
    yAxisOptionGroups,
    bubbleRepresents,
    bubbleRepresentsOptionGroups,
}: PackArchitectureChartTitleInput): ChartTitleSegment[] => {
    const xLabel = getAxisLabel(xAxis, xAxisOptionGroups);
    const yLabel = getAxisLabel(yAxis, yAxisOptionGroups);
    const bubbleLabel = getAxisLabel(bubbleRepresents, bubbleRepresentsOptionGroups);
    const yGroup = getAxisGroupTitle(yAxis, yAxisOptionGroups) || yLabel.split(/\s+/)[0] || '';
    const xGroup = getAxisGroupTitle(xAxis, xAxisOptionGroups).trim();
    const isKpiXAxis = getXAxisColumnOptions(xAxisConfig).some((item) => item.id === xAxis);
    const segments: ChartTitleSegment[] = [];
    const bold = (text: string) => segments.push({ text, emphasis: true });
    const plain = (text: string) => segments.push({ text });

    if (isKpiXAxis) {
        if (yGroup) bold(yGroup);
        if (xGroup && xGroup.toLowerCase() !== yGroup.toLowerCase()) {
            if (segments.length) plain(' ');
            bold(xGroup);
        }
        plain(' Architecture');
    } else {
        if (yGroup) bold(yGroup);
        plain(' Architecture');
        if (xLabel) {
            plain(' of ');
            bold(xLabel);
        }
    }

    if (bubbleLabel) {
        plain(' by ');
        bold(bubbleLabel);
    }

    return segments;
};

export const buildPackArchitectureChartTitle = (input: PackArchitectureChartTitleInput) =>
    buildPackArchitectureChartTitleSegments(input)
        .map((segment) => segment.text)
        .join('');

export const buildPackArchitectureTableTitle = (xAxisLabel: string, bubbleRepresentsLabel: string) => {
    if (xAxisLabel && bubbleRepresentsLabel) {
        return `${xAxisLabel} by ${bubbleRepresentsLabel}`;
    }
    if (xAxisLabel) return xAxisLabel;
    return 'Data Table';
};

export { buildPpaChartRequest, isKpiXAxisValue } from './packArchitectureChartRequest.helpers';

export {
    buildKpiAxisControlsMeta,
    getKpiAxisConfigurationDefaultsFromMeta,
    getKpiBinLabelAffixesForXAxis,
    getKpiBinLabelAffixesFromColumnOption,
} from './packArchitectureMeta.kpiAxis.helpers';
